/**
 * Testimonials Variants
 *
 * CVA variants for the Testimonials component.
 * Exported separately to comply with React Fast Refresh requirements.
 *
 * @see Testimonials.tsx for component implementation
 */

import {cva} from 'class-variance-authority';

/**
 * Testimonial card style variants using CVA.
 * All colors use semantic tokens from the design system.
 * Updated with 2025 premium design trends.
 */
export const testimonialCardVariants = cva(
  // Base styles - Mobile first with premium design
  [
    "relative flex flex-col group",
    "p-6 sm:p-7 lg:p-8",
    "rounded-3xl",
    "transition-all duration-500 ease-out",
    "w-full h-full",
    // Premium glass effect with subtle gradient
    "bg-gradient-to-br from-background/80 via-background/60 to-muted/30",
    "dark:from-surface/60 dark:via-surface/40 dark:to-surface/60",
    "backdrop-blur-xl",
    "border border-border/40 dark:border-border/20",
    // Hover states
    "hover:border-border/70 dark:hover:border-border/40",
    "hover:shadow-2xl hover:shadow-black/5 dark:hover:shadow-black/30",
    "lg:hover:-translate-y-1.5",
    // Inner glow effect
    "shadow-[inset_0_1px_0_0_hsl(0_0%_100%_/_0.05)]",
  ].join(" "),
  {
    variants: {
      variant: {
        default: "",
        bordered: [
          "bg-transparent border-2 border-border/50",
          "dark:border-border/40",
        ].join(" "),
        elevated: [
          // Enhanced hover gradient
          "hover:from-primary/10 hover:via-background/60 hover:to-accent/10",
          "dark:hover:from-primary/15 dark:hover:via-surface/50 dark:hover:to-accent/15",
        ].join(" "),
        minimal: [
          "bg-transparent border-0",
        ].join(" "),
      },
      featured: {
        true: [
          // Featured card with gradient border glow
          "border-primary/30 dark:border-primary/25",
          "shadow-lg shadow-primary/10 dark:shadow-primary/5",
          "from-primary/10 via-background/70 to-accent/10",
          "dark:from-primary/20 dark:via-surface/50 dark:to-accent/15",
        ].join(" "),
        false: "",
      },
    },
    defaultVariants: {
      variant: "elevated",
      featured: false,
    },
  }
);

/**
 * Testimonials section style variants using CVA.
 * Updated with premium design patterns.
 */
export const testimonialsVariants = cva("relative w-full overflow-hidden", {
  variants: {
    background: {
      transparent: "bg-transparent",
      muted: [
        "bg-muted/30",
        "dark:bg-background",
      ].join(" "),
      surface: [
        "bg-surface",
        "dark:bg-surface",
      ].join(" "),
      primary: [
        "bg-primary/5",
        "dark:bg-primary/5",
      ].join(" "),
      gradient: [
        "bg-gradient-to-b from-background via-muted/20 to-background",
        "dark:from-background dark:via-surface/30 dark:to-background",
      ].join(" "),
    },
    layout: {
      grid: "",
      carousel: "",
      stack: "",
      featured: "",
    },
    spacing: {
      sm: "py-8 sm:py-12",
      md: "py-12 sm:py-16 lg:py-20",
      lg: "py-16 sm:py-20 lg:py-28",
    },
  },
  defaultVariants: {
    background: "muted",
    layout: "grid",
    spacing: "md",
  },
});
